// The session diagnostic log (qa-runs 1227): what both sides agree on, and the one redaction both run. A browser cleans
// a row before sending it and the server cleans it again, so a secret has to get past the same rules twice.
/** One socket's state of the switch: sent only when it is on or was just turned off. `ticket` lets this person's browser post its rows. */
export const TABLE_DIAG = 'table:diag';
/** The browser's latency check while recording: answered, and nothing else. */
export const TABLE_LATENCY = 'table:latency';
export const DIAG_LIMITS = {
    /** The most one table keeps; past either, its oldest rows go. */
    rowsPerTable: 300_000,
    bytesPerTable: 200 * 1024 * 1024,
    maxAgeDays: 30,
    /** The most one row's payload holds, as JSON. */
    payloadBytes: 8 * 1024,
    textChars: 2000,
    listItems: 50,
    depth: 8,
    /** The most rows one person's browsers may send a minute, and in one post. */
    clientRowsPerMinute: 2000,
    clientRowsPerPost: 500,
    /** How far back a browser may date a row, in ms: what it held before it could send. */
    clientAgeMs: 60 * 60_000,
};
/** What the DM is shown and passes along: the start of the recording's id, which the reading script finds it by. */
export const diagHash = (sessionId) => sessionId.slice(0, 8);
/** The kinds `--errors` asks for: refused actions, failures of any sort, and dropped connections. */
export const DIAG_ERROR_KINDS = [
    'refused', 'http.error', 'disconnect', 'console.error', 'uncaught', 'site.failed', 'diag.dropped',
    'client.error', 'client.rejection', 'client.react', 'client.console.error', 'client.fetch.failed', 'client.asset', 'client.socket.error',
];
const REDACTED = '[redacted]';
export const TRUNCATED_MARK = '__truncated';
/** Key names whose value is never kept, whatever it holds. */
const SECRET_KEY = /(jwt|bearer|passw|secret|cookie|webhook|handoff|p256dh|vapid|access.?key|api.?key|push.?key|authorization|subscription)/i;
const SECRET_EXACT = /^(auth|code|ticket|endpoint)$/i;
/** `token` also means a piece on the board here: a string under such a key goes unless it is an id; an object is walked. */
const TOKEN_KEY = /token/i;
/** `"password":"x"` or `password=x` inside text: an object a console line already turned into words. */
const SECRET_PAIR = /(["']?[\w-]*(?:passw|secret|jwt|bearer|cookie|webhook|handoff|p256dh|vapid|access.?key|api.?key|push.?key|authorization|token)[\w-]*["']?\s*[:=]\s*)("[^"]*"|'[^']*'|[^\s,;&}]+)/gi;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Takes secrets out of free text (an error's message, a URL) by their shape, and what Postgres would refuse in JSON. */
export function scrubText(text) {
    return text
        .replace(/\u0000|\p{Cs}/gu, '�')
        .replace(/\beyJ[\w-]{6,}\.[\w-]{6,}\.[\w-]*/g, REDACTED)
        .replace(/\b(bearer)\s+[\w.~+/=-]{8,}/gi, `$1 ${REDACTED}`)
        .replace(/https?:\/\/[^\s"']*\/api\/webhooks\/[^\s"']*/gi, REDACTED)
        .replace(SECRET_PAIR, `$1${REDACTED}`)
        .replace(/([?&#](?:token|access_token|refresh_token|code|handoff|key|ticket)=)[^&#\s"']*/gi, `$1${REDACTED}`);
}
function cleanText(text, walk) {
    if (text.startsWith('data:'))
        return `[data uri, ${text.length} characters]`;
    if (text.length > 512 && /^[A-Za-z0-9+/=\s]+$/.test(text))
        return `[encoded data, ${text.length} characters]`;
    const clean = scrubText(text);
    if (clean.length <= DIAG_LIMITS.textChars)
        return clean;
    walk.cut = true;
    return `${clean.slice(0, DIAG_LIMITS.textChars).replace(/\p{Cs}$/u, '')}… (${clean.length} characters)`;
}
function clean(value, depth, walk) {
    if (value === null || typeof value === 'boolean')
        return value;
    if (typeof value === 'number')
        return Number.isFinite(value) ? value : String(value);
    if (typeof value === 'string')
        return cleanText(value, walk);
    if (typeof value === 'bigint')
        return String(value);
    if (typeof value !== 'object')
        return undefined;
    if (value instanceof Error)
        return { name: value.name, message: cleanText(value.message, walk), stack: cleanText(value.stack ?? '', walk) };
    if (value instanceof Date)
        return value.toISOString();
    if (value instanceof ArrayBuffer)
        return { bytes: value.byteLength };
    if (ArrayBuffer.isView(value))
        return { bytes: value.byteLength };
    if ((walk.nodes += 1) > 2000 || depth >= DIAG_LIMITS.depth) {
        walk.cut = true;
        return '…';
    }
    if (Array.isArray(value)) {
        const kept = value.slice(0, DIAG_LIMITS.listItems).map((v) => clean(v, depth + 1, walk) ?? null);
        if (value.length > DIAG_LIMITS.listItems) {
            walk.cut = true;
            kept.push(`… (${value.length} items)`);
        }
        return kept;
    }
    const out = {};
    for (const [key, v] of Object.entries(value)) {
        if (SECRET_KEY.test(key) || SECRET_EXACT.test(key)) {
            out[key] = REDACTED;
            continue;
        }
        if (TOKEN_KEY.test(key) && typeof v === 'string' && !UUID.test(v)) {
            out[key] = REDACTED;
            continue;
        }
        const kept = clean(v, depth + 1, walk);
        if (kept !== undefined)
            out[scrubText(key)] = kept;
    }
    return out;
}
/** A value as a row's payload: redacted, cut to the limits with a mark, and as JSON that Postgres will take. */
export function diagPayload(value) {
    const walk = { nodes: 0, cut: false };
    const cleaned = clean(value, 0, walk) ?? null;
    const shaped = (cleaned !== null && typeof cleaned === 'object' && !Array.isArray(cleaned) ? cleaned : { value: cleaned });
    let json = JSON.stringify(walk.cut ? { ...shaped, [TRUNCATED_MARK]: true } : shaped);
    if (json.length > DIAG_LIMITS.payloadBytes) {
        const head = json.slice(0, DIAG_LIMITS.payloadBytes - 200).replace(/\p{Cs}$/u, '');
        json = JSON.stringify({ [TRUNCATED_MARK]: true, characters: json.length, head });
    }
    return { json, size: json.length };
}
//# sourceMappingURL=diagnostics.js.map